"use client";

import clsx from "clsx";
import { isPublic } from "@/lib/profile";
import { PrivateOnlyMark } from "@/components/scene/PrivateOnlyMark";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 1.3 — Authorized individuals + AML.
 * Vega introduces herself: "Running AML screening now."
 *   3-second progress arc resolves to a green AMLScreened: PASS chip.
 * Risk module lights up.
 *
 * On the public profile the AML column is hidden — only the authorized-
 * individuals card renders, and the layout collapses to a single column.
 */
export function AmlScreen({ amlPass = true, className }: { amlPass?: boolean; className?: string }) {
  const hideAml = isPublic();
  const t = useTranslations("t1Registration");
  return (
    <div className={clsx("grid h-full gap-4", hideAml ? "grid-cols-1" : "grid-cols-2", className)}>
      <div className="glass p-5">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("amlStep")}</p>
        <h3 className="mt-1 font-display text-base font-semibold text-orbitra-paper">{t("amlProcessingDirector")}</h3>

        <div className="mt-4 rounded-md border border-state-verified/30 bg-orbitra-ink/40 p-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-state-verified text-[12px] font-bold text-orbitra-ink">FA</span>
            <div className="flex flex-col">
              <p className="text-[13px] font-semibold text-orbitra-paper" dir="ltr">{t("amlSignatoryName")}</p>
              <p className="text-[10px] text-orbitra-muted">{t("amlSignatoryRole")}</p>
            </div>
            <span className="ms-auto rounded-full border border-state-verified/40 bg-state-verified/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-state-verified">
              {t("amlEidParsedTag")}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
            <div className="rounded border border-state-verified/15 bg-orbitra-ink/50 px-2 py-1.5">
              <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("amlFieldEmiratesId")}</p>
              <p className="mt-0.5 font-mono text-orbitra-paper" dir="ltr">784-1988-•••••••-3</p>
            </div>
            <div className="rounded border border-state-verified/15 bg-orbitra-ink/50 px-2 py-1.5">
              <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("amlFieldNationality")}</p>
              <p className="mt-0.5 text-orbitra-paper">{t("amlNationalityValue")}</p>
            </div>
            <div className="rounded border border-state-verified/15 bg-orbitra-ink/50 px-2 py-1.5">
              <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("amlFieldPhone")}</p>
              <p className="mt-0.5 font-mono text-orbitra-paper" dir="ltr">+971 50 412 ••• 4</p>
            </div>
            <div className="rounded border border-state-verified/15 bg-orbitra-ink/50 px-2 py-1.5">
              <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("amlFieldEmail")}</p>
              <p className="mt-0.5 text-orbitra-paper" dir="ltr">{t("amlSignatoryEmail")}</p>
            </div>
            <div className="col-span-2 rounded border border-state-verified/15 bg-orbitra-ink/50 px-2 py-1.5">
              <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{t("amlFieldResidency")}</p>
              <p className="mt-0.5 text-orbitra-paper">{t("amlResidencyValue")}</p>
            </div>
          </div>
        </div>
      </div>

      {!hideAml && (
      <PrivateOnlyMark className="flex h-full flex-col">
      <div className="glass flex h-full flex-col p-5">
        <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("amlScreeningTitle")}</p>
        <p className="mt-1 text-[12px] text-orbitra-teal-soft">{t("amlScreeningDescription")}</p>

        <div className="mt-4 flex-1 rounded-md border border-state-verified/30 bg-orbitra-ink/40 p-4">
          <ul className="flex flex-col gap-2 text-[11px]">
            <ScreenLine label={t("amlCheckOfac")}         status="PASS" />
            <ScreenLine label={t("amlCheckUaeSanctions")} status="PASS" />
            <ScreenLine label={t("amlCheckPep")}          status="PASS" />
          </ul>
        </div>

        <div className={clsx(
          "mt-4 inline-flex items-center gap-2 self-start rounded-full border px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider",
          amlPass
            ? "border-policy-pass/50 bg-policy-pass/15 text-policy-pass"
            : "border-policy-fail/50 bg-policy-fail/15 text-policy-fail",
        )}>
          {amlPass ? t("amlScreenedPass") : t("amlScreenedFail")}
        </div>
      </div>
      </PrivateOnlyMark>
      )}
    </div>
  );
}

function ScreenLine({ label, status }: { label: string; status: "PASS" | "FAIL" }) {
  const ok = status === "PASS";
  return (
    <li className="flex items-center justify-between rounded border border-state-verified/15 bg-orbitra-ink/50 px-2 py-1.5">
      <span className="text-orbitra-paper">{label}</span>
      <span className={clsx("font-mono text-[10px] font-semibold", ok ? "text-policy-pass" : "text-policy-fail")}>
        {status}
      </span>
    </li>
  );
}
